import React from 'react';
import { LABEL, TagDisplayV2, BlockLabel, colors } from "oolib";
import { StyledSectionTagsWrapper, StyledTagsWrapper } from '../styled';
import TagsInputMulti from '../../../../../components/inputs/DynamicTagsInputs/TagsInputMulti';

// Helper to check if a metadata value exists and has content
const hasContent = (value) => {
  if (!value) return false;
  if (value.data && Array.isArray(value.data)) return value.data.length > 0;
  if (typeof value === 'object' && value.display) return true;
  return !!value;
};

// Helper to render the appropriate tag component based on metadata type
const renderTagComponent = (key, value, label) => {
  // Handle array-based tag data (with data property)
  if (value.data && Array.isArray(value.data)) {
    if (value.data.length === 1) {
      // For single item arrays like country, state, district
      return (
        <div key={key}>
          <BlockLabel label={label} />
          <TagDisplayV2 display={value.data[0].display} />
        </div>
      );
    } else {
      // For multi-item arrays like subject, stakeholder
      return (
        <TagsInputMulti
          key={key}
          label={label}
          readOnly
          value={value}
        />
      );
    }
  }
  
  // Handle object with display property (like facilityType)
  if (typeof value === 'object' && value.display) {
    return (
      <div key={key}>
        <BlockLabel label={label} />
        <TagDisplayV2 display={value.display} />
      </div>
    );
  }
  
  // Handle simple string values
  return (
    <div key={key}>
      <BlockLabel label={label} />
      <TagDisplayV2 display={value} />
    </div>
  );
};

const TagsSection = ({ activeItem, allTpls, fieldConfig }) => {
  // Skip rendering if no metadata or fieldConfig
  if (!activeItem.metaData || !fieldConfig) return null;
  
  // Get metadata fields configuration from tenant config
  const metadataFields = fieldConfig.metadataFields || {};
  
  // Check if any metadata exists (excluding contentType which is handled separately)
  const hasAnyMetadata = Object.keys(metadataFields)
    .filter(key => key !== 'contentType')
    .some(key => hasContent(activeItem.metaData[key]));
  
  // If no metadata exists, don't render the section
  if (!hasAnyMetadata && !activeItem.metaData.contentType) return null;

  return (
    <StyledSectionTagsWrapper>
      <div style={{ position: 'sticky', top: 0, backgroundColor: colors.greyColor5, padding: '1rem 0', width: '100%', zIndex: 1 }}>
        <LABEL>TAGS</LABEL>
      </div>
      <StyledTagsWrapper>
        {/* Content Type is always rendered if available */}
        {activeItem.metaData.contentType && (
          <div>
            <BlockLabel label="Content Type"/>
            <TagDisplayV2
              display={
                allTpls.find(
                  (d) => d.kp_content_type === activeItem.metaData.contentType
                )?.general?.content?.title || 'Unknown'
              }
            />
          </div>
        )}
        
        {/* Dynamically render other metadata fields */}
        {Object.entries(metadataFields).map(([key, path]) => {
          // Skip contentType as it's handled separately
          if (key === 'contentType') return null;
          
          const value = activeItem.metaData[key];
          if (!hasContent(value)) return null;
          
          // Convert key to display label (e.g., 'facilityType' -> 'Facility Type')
          const label = key
            .replace(/([A-Z])/g, ' $1') // Insert a space before capital letters
            .replace(/^./, str => str.toUpperCase()) // Capitalize first letter
            .trim();
            
          return renderTagComponent(key, value, label);
        })}
      </StyledTagsWrapper>
    </StyledSectionTagsWrapper>
  );
};

export default TagsSection;
